import { useEffect, useState, useRef } from "react";
import SiteNav from "../components/SiteNav";
import { InfoFooter } from "../components/InfoLayout";
import SlCloudShell from "../brands/servicelad/SlCloudShell";
import usePageMeta from "../hooks/usePageMeta";
import { cleanRegistration, formatUkRegistration, isValidUkRegistration } from "../utils/registration";
import v5cCss from "../styles/v5c-alerts.css?raw";
import { faqsFor, reasons, steps, v5cSoloBrands } from "../content/v5cAlerts";
import { packagesFor } from "../config/pricing.js";

const tiers = [
  { months: 3, price: "£1.49", note: "Starter", perMonth: "£0.50" },
  { months: 6, price: "£2.49", note: "Save 16%", perMonth: "£0.42", featured: true },
  { months: 12, price: "£3.99", note: "Best value", perMonth: "£0.33" },
];

const brandCopy = {
  serviceboss: { metaTitle: "V5C Logbook Alerts UK | ServiceBoss", metaDescription: "Get an instant alert when a vehicle's DVLA V5C logbook issue date changes. Daily monitoring, one-off payment, no subscription." },
  servicebyreg: { metaTitle: "V5C Logbook Change Alerts | ServiceByReg", metaDescription: "Monitor a UK vehicle's V5C logbook issue date daily and get alerted the moment it changes. One payment, no auto-renewal." },
  servicelad: { metaTitle: "V5C Logbook Alerts and DVLA Monitoring | ServiceLad", metaDescription: "Watch a UK vehicle's V5C issue date with ServiceLad. Daily checks, an email the day the logbook changes. One payment, no subscription." },
};

export default function V5cAlertsPage({ brand }) {
  const copy = brandCopy[brand.key] || brandCopy.serviceboss;
  const soloAllowed = v5cSoloBrands.includes(brand.key);
  const [riskPlan, completePlan] = packagesFor(brand.key);
  const faqs = faqsFor(brand.key);
  // Arriving from a vehicle page (?reg=) skips straight to the options for that car.
  const [linkedReg] = useState(() => {
    const raw = cleanRegistration(new URLSearchParams(window.location.search).get("reg") || "");
    return isValidUkRegistration(raw) ? raw : "";
  });
  const [registration, setRegistration] = useState(linkedReg);
  const [error, setError] = useState("");
  const [stage, setStage] = useState(linkedReg ? "choose" : "reg");
  const [v5cTerm, setV5cTerm] = useState(3);
  const canonical = `https://${brand.domain}/v5c-alerts`;

  usePageMeta({ title: copy.metaTitle, description: copy.metaDescription, canonical });

  const validateReg = () => {
    const clean = cleanRegistration(registration);
    if (!isValidUkRegistration(clean)) {
      setError("Enter a valid UK registration.");
      return false;
    }
    return true;
  };

  const goCheckout = (path, term) => {
    const clean = cleanRegistration(registration);
    const params = new URLSearchParams({ vrm: clean });
    if (path === "v5c") params.set("v5c", String(term || v5cTerm));
    else params.set("v5c", String(v5cTerm));
    // Without a plan, checkout defaults to Complete, so the Risk Check button would land on the dearer report.
    if (path === "risk" || path === "complete") params.set("plan", path);
    else if (path === "v5c") params.set("plan", "v5c");
    window.location.href = `/checkout?${params.toString()}`;
  };

  const scopeCardRef = useRef(null);

  useEffect(() => {
    if (!linkedReg) return undefined;
    // On a phone the options sit below the reg box. Wait for the hero to lay
    // out, then bring them into view, leaving room for the sticky header.
    const timer = window.setTimeout(() => {
      const panel = document.querySelector(".options-panel");
      if (panel && panel.getBoundingClientRect().top > window.innerHeight * 0.6) {
        // The page may scroll inside a wrapper, so scrollIntoView rather than window.scrollTo.
        panel.style.scrollMarginTop = "72px";
        panel.scrollIntoView({ block: "start" });
      }
    }, 250);
    return () => window.clearTimeout(timer);
  }, [linkedReg]);

  const submitReg = (event) => {
    event.preventDefault();
    if (validateReg()) {
      setError("");
      setStage("choose");
      setTimeout(() => {
        scopeCardRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 50);
    }
  };

  const back = () => setStage("reg");

  // The term cards pick a watch length; this sends it on. Without a valid reg
  // yet, take the visitor to the reg box first.
  const startWatch = () => {
    if (!isValidUkRegistration(cleanRegistration(registration))) {
      setError("Enter the registration you want to watch.");
      scopeCardRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      window.setTimeout(() => document.getElementById("v5c-reg")?.focus({ preventScroll: true }), 350);
      return;
    }
    if (soloAllowed) {
      goCheckout("v5c", v5cTerm);
      return;
    }
    setError("");
    setStage("choose");
    window.setTimeout(() => scopeCardRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 50);
  };

  return (
    <SlCloudShell brand={brand}>
      <style>{v5cCss}</style>
      <SiteNav brand={brand} />

      <main className="v5c-main">
        <section className="watchtower">
          <div className="hero-bg" aria-hidden="true">
            <div className="hero-glow hero-glow-cyan" />
            <div className="hero-glow hero-glow-lime" />
            <div className="hero-glow hero-glow-cream" />
            <div className="hero-radar-ring r1" />
            <div className="hero-radar-ring r2" />
            <div className="hero-radar-ring r3" />
          </div>
          <div className="hero-car" aria-hidden="true"><picture><source srcSet="/v5chero.webp" type="image/webp" /><img src="/v5chero.png" alt="" /></picture></div>
          <div className="wrap watchtower-grid">
            <div className="watchtower-copy">
              <span className="v5c-eyebrow">V5C logbook alerts</span>
              <h1>Know the moment the <span className="logbook-word">logbook</span> moves.</h1>
              <p className="hero-subline">Daily DVLA checks. One alert when it matters.</p>
              <ul className="watch-badges">
                <li><span className="badge-icon" aria-hidden="true">◔</span>Checked daily</li>
                <li><span className="badge-icon" aria-hidden="true">✉</span>Email included</li>
                <li><span className="badge-icon" aria-hidden="true">○</span>One-off payment</li>
              </ul>
            </div>

            <form className="scope-card" ref={scopeCardRef} onSubmit={submitReg} noValidate>
              <div className="watch-icon" aria-hidden="true">⋏</div>
              <label htmlFor="v5c-reg">Enter a UK registration</label>
              <div className="v5c-plate-input">
                <span className="v5c-plate-band"><img src="/gb-flag.svg" alt="" />UK</span>
                <input
                  id="v5c-reg"
                  value={registration}
                  maxLength="8"
                  autoComplete="off"
                  inputMode="text"
                  placeholder="ENTER REG"
                  aria-describedby="v5c-error"
                  onChange={(event) => { setRegistration(cleanRegistration(event.target.value)); setError(""); }}
                />
              </div>
              <button type="submit">Check this reg <span aria-hidden="true">→</span></button>
              <div className="v5c-form-error" id="v5c-error" role="alert">{error}</div>
              <small className="v5c-form-help">We check the vehicle first, then you pick your watch.</small>
            </form>

            {stage === "choose" && (
              <div className="options-panel">
                <div className="options-head">
                  <span className="options-reg">{formatUkRegistration(cleanRegistration(registration))}</span>
                  <button type="button" className="options-back" onClick={back}>← Change reg</button>
                </div>

                {soloAllowed && (<>
                <div className="option-path">
                  <div className="option-path-head"><span className="path-tag">Watch only</span><h3>Just the V5C alert</h3><p>Monitor the logbook date for the period you choose. No full report.</p></div>
                  <div className="v5c-term-row">
                    {[3, 6, 12].map((m) => (
                      <button key={m} type="button" className={`term-pill${v5cTerm === m ? " selected" : ""}`} onClick={() => setV5cTerm(m)}>
                        <b>{m} months</b>
                        <span>{m === 3 ? "£1.49" : m === 6 ? "£2.49" : "£3.99"}</span>
                      </button>
                    ))}
                  </div>
                  <button type="button" className="option-cta" onClick={() => goCheckout("v5c")}>Watch V5C for {v5cTerm} months <span aria-hidden="true">→</span></button>
                </div>

                <div className="option-divider"><span>or add a full report</span></div>
                </>)}

                <div className="option-path">
                  <div className="option-path-head"><span className="path-tag alt">Report + watch</span><h3>{riskPlan.name} + V5C</h3><p>Stolen, write-off, finance and export checks plus {v5cTerm} months of logbook monitoring.</p></div>
                  <button type="button" className="option-cta" onClick={() => goCheckout("risk")}>Risk Check + V5C <span aria-hidden="true">→</span></button>
                </div>

                <div className="option-path">
                  <div className="option-path-head"><span className="path-tag alt">Report + watch</span><h3>{completePlan.name} + V5C</h3><p>Everything in the {riskPlan.name} plus manufacturer service history, with {v5cTerm} months of monitoring.</p></div>
                  <button type="button" className="option-cta featured" onClick={() => goCheckout("complete")}>Complete + V5C <span aria-hidden="true">→</span></button>
                </div>
              </div>
            )}
          </div>
          <div className="hero-alert-preview" aria-hidden="true">
            <div className="alert-mock-bar"><span className="alert-dot" />{brand.name} Logbook Alert</div>
            <div className="alert-mock-body">
              <strong>The V5C issue date for <em>AB12 CDE</em> has changed on DVLA's record.</strong>
              <span>Checked just now. Previous date no longer matches.</span>
            </div>
            <div className="alert-mock-foot">@ Email sent to you. Instantly.</div>
          </div>
        </section>

        <section className="v5c-section">
          <div className="wrap">
            <div className="v5c-section-head">
              <span className="v5c-kicker">How it works</span>
              <h2>Four steps. One clear signal.</h2>
              <p>Choose your watch once. We handle the daily checks.</p>
            </div>
            <div className="steps-grid">
              {steps.map((step) => (
                <article className="step-card" key={step.n}>
                  <span className="step-num">{step.n}</span>
                  <h3>{step.title}</h3>
                  <p>{step.body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="v5c-section v5c-reasons">
          <div className="wrap">
            <div className="v5c-section-head">
              <span className="v5c-kicker">What a change means</span>
              <h2>Why would the date move?</h2>
              <p>DVLA has reissued or updated the logbook record. There are several possible reasons.</p>
            </div>
            <div className="reasons-grid">
              {reasons.map((reason) => (
                <article className="reason-card" key={reason.n}>
                  <span className="reason-num">{reason.n}</span>
                  <h3>{reason.title}</h3>
                  <p>{reason.body}</p>
                </article>
              ))}
            </div>
            <aside className="reason-caveat">
              <strong>What the alert cannot tell you</strong>
              <p>It cannot show who the keeper is or prove why the V5C changed. It only confirms the on-record issue date moved.</p>
            </aside>
          </div>
        </section>

        <section className="v5c-section v5c-pricing">
          <div className="wrap">
            <div className="v5c-section-head">
              <span className="v5c-kicker">Pick your watch</span>
              <h2>One reg. One payment. Daily checks.</h2>
              <p>Email included. No subscription. No auto-renewal. {soloAllowed ? "Buy it on its own or add it to a report." : "Added at checkout alongside your report."}</p>
            </div>
            <div className="tiers-grid" role="radiogroup" aria-label="How long to watch">
              {tiers.map((tier) => (
                <button
                  type="button"
                  role="radio"
                  aria-checked={v5cTerm === tier.months}
                  className={`tier-card${tier.featured ? " tier-featured" : ""}${v5cTerm === tier.months ? " is-selected" : ""}`}
                  key={tier.months}
                  onClick={() => setV5cTerm(tier.months)}
                >
                  <span className="tier-tick" aria-hidden="true">✓</span>
                  {tier.featured && <span className="tier-flag">Save 16%</span>}
                  <span className="tier-months">{tier.months} months</span>
                  <strong className="tier-price">{tier.price}</strong>
                  <small className="tier-note">{tier.note}</small>
                  <span className="tier-per">≈ {tier.perMonth}/month</span>
                  <span className="tier-type">{soloAllowed ? "One-off payment" : "One-off add-on"}</span>
                </button>
              ))}
            </div>
            <div className="tier-cta-row">
              <button type="button" className="tier-cta" onClick={startWatch}>
                Watch for {v5cTerm} months, {tiers.find((tier) => tier.months === v5cTerm)?.price} <span aria-hidden="true">→</span>
              </button>
            </div>
            <p className="pricing-note">{soloAllowed ? "Buy the watch on its own or add it to a report at checkout." : "The V5C watch is added to your report at checkout."} Pay once. No subscription. No auto-renewal.</p>
          </div>
        </section>

        <section className="v5c-section v5c-faq">
          <div className="wrap faq-wrap">
            <div className="v5c-section-head">
              <span className="v5c-kicker">Quick answers</span>
              <h2>Tap a question to open it.</h2>
            </div>
            <div className="faq-list">
              {faqs.map((faq, i) => (
                <details className="faq-item" key={i}>
                  <summary>{faq.q}</summary>
                  <p>{faq.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="v5c-final">
          <div className="wrap">
            <div className="final-card">
              <div className="watch-icon big" aria-hidden="true">⋏</div>
              <h2>Ready when you are.</h2>
              <p>Start with the number plate. We show the vehicle first, then you pick your watch and any report.</p>
              <form className="final-form" onSubmit={submitReg} noValidate>
                <div className="v5c-plate-input">
                  <span className="v5c-plate-band"><img src="/gb-flag.svg" alt="" />UK</span>
                  <input
                    value={registration}
                    maxLength="8"
                    autoComplete="off"
                    inputMode="text"
                    placeholder="ENTER REG"
                    aria-label="Vehicle registration"
                    onChange={(event) => { setRegistration(cleanRegistration(event.target.value)); setError(""); }}
                  />
                </div>
                <button type="submit">Check this reg <span aria-hidden="true">→</span></button>
              </form>
              {error && <div className="v5c-form-error" role="alert">{error}</div>}
            </div>
          </div>
        </section>
      </main>
      <InfoFooter brand={brand} />
    </SlCloudShell>
  );
}
